import { useEffect, useState } from 'react'
import { useEffectOnce } from 'react-use'
import { useParams } from 'lib/router'
import {
  usePublishOrganizationOnboarding,
  useGetOrganizationOnboarding,
} from 'api/endpoints/organizations'
import { useHistory } from 'react-router-dom'
import Complete from 'views/Onboarding/Complete'
import PublishError from 'views/Onboarding/PublishError'
import { useStore } from 'state/store'
import { AxiosResponse } from 'axios'
import { mapToOrganization } from 'api/mappers/organization'
import { Organization, OrganizationOnboardingDetails } from 'lib/generated/api'
import CompanyExists from 'views/Onboarding/CompanyExists'
import { UseMutationResult, UseQueryResult } from 'react-query'
import { GlobalShell } from 'components/GlobalShell'
import { useMessage } from 'contexts/MessageContext'
import { useContent } from 'lib/content/react'

export const OnboardingPublishContainer = () => {
  useEffect(() => {
    document.body.classList.add('off-white-background')
    return () => {
      document.body.classList.remove('off-white-background')
    }
  }, [])

  const { onboardingRecordId } = useParams<{ onboardingRecordId: string }>()
  const publishRecord = usePublishOrganizationOnboarding() as UseMutationResult<
    Organization,
    AxiosResponse
  >
  const organizationOnboardingData = useGetOrganizationOnboarding({
    onboardingRecordId,
  }) as UseQueryResult<OrganizationOnboardingDetails, any>

  const onboardingRecord = useGetOrganizationOnboarding({ onboardingRecordId })
  const [publishError, setPublishError] = useState(false)
  const [companyExists, setCompanyExists] = useState(false)
  const [publishedOrg, setPublishedOrg] = useState<Organization | undefined>()
  const history = useHistory()
  const setOrganization = useStore(state => state.setOrganization)
  const { addError } = useMessage()
  const { formatMessage } = useContent()

  useEffectOnce(() => {
    if (!onboardingRecordId) return

    publishRecord.mutate(
      { onboardingRecordId: onboardingRecordId },
      {
        onSuccess: setPublishedOrg,
        onError: error => {
          switch (error.status) {
            case 409:
              setCompanyExists(true)
              break
            default:
              addError({
                description: formatMessage({ id: 'pmportal.error.unknown' }),
              })
              setPublishError(true)
              break
          }
        },
      }
    )
  })

  useEffect(() => {
    if (publishedOrg) {
      if (!onboardingRecord.data) return

      // Switch to the newly created Org
      setOrganization(mapToOrganization(publishedOrg))

      if (onboardingRecord.data.connectionId) {
        history.push(
          `/?connectionId=${onboardingRecord.data.connectionId}&notice=workspaceConnected`
        )
      } else {
        history.push('/')
      }
    }
  }, [onboardingRecord, publishedOrg, history, setOrganization])

  return (
    <GlobalShell>
      {publishError ? (
        <PublishError
          onBack={() => {
            history.push(`/welcome/${onboardingRecordId}/form`)
          }}
        />
      ) : companyExists ? (
        <CompanyExists
          companyName={organizationOnboardingData?.data?.organizationName}
          onExit={() =>
            (window.location.href = 'https://ritual.co/products/teams')
          }
        />
      ) : (
        <Complete />
      )}
    </GlobalShell>
  )
}
